/**
 * @sazeo/sdk — Candidate premium hooks
 *
 * Candidate-facing hooks for paid CV pre-check and tailoring fulfillment.
 * Data is read through the app/candidate-premium Edge Function with the user
 * JWT forwarded, so candidate_premium_jobs RLS remains the source of truth.
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import type { UseQueryResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

export type CandidatePremiumJobType = 'precheck' | 'tailoring';
export type CandidatePremiumJobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
export type CandidatePremiumProduct = 'precheck_single' | 'precheck_triple' | 'tailoring';

export interface CandidatePremiumFileResult {
  bucket: string;
  expires_in_seconds: number;
  filename?: string;
  format: string;
  mime_type: string;
  signed_url: string | null;
  storage_key: string;
  url?: string;
}

export interface CandidatePremiumJob {
  amount_minor: number;
  completed_at: string | null;
  created_at: string;
  currency: string;
  error_message: string | null;
  id: string;
  input_payload: Record<string, unknown> & {
    candidate_premium?: { pack_size?: number; product?: string; source_job_slug?: string };
    vacancy_title?: string;
  };
  job_type: CandidatePremiumJobType;
  payment_order_id: string;
  queued_at: string;
  result_payload: Record<string, unknown> & {
    changes?: string[];
    files?: CandidatePremiumFileResult[];
    honest_advice?: string;
    kind?: 'candidate_precheck_result' | 'candidate_tailoring_result';
    match_score?: number;
    missing_skills?: string[];
    original_text?: string;
    suggestions?: string[];
    tailored_text?: string;
    warnings?: string[];
  };
  started_at: string | null;
  status: CandidatePremiumJobStatus;
  updated_at: string;
}

export interface CandidatePremiumJobsInput {
  enabled?: boolean;
  jobType?: CandidatePremiumJobType;
  limit?: number;
  paymentOrderId?: string;
  product?: CandidatePremiumProduct;
  status?: CandidatePremiumJobStatus;
}

export interface StartCandidatePremiumPrecheckSlotInput {
  parentJobId: string;
  sourceJobSlug?: string | null;
}

export class CandidatePremiumApiError extends Error {
  code?: string;
  requestId?: string;
  status: number;

  constructor(message: string, status: number, code?: string, requestId?: string) {
    super(message);
    this.name = 'CandidatePremiumApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

type CandidatePremiumSupabaseClient = SupabaseClientLike | null | undefined;

export const candidatePremiumKeys = {
  all: ['candidate-premium'] as const,
  lists: () => [...candidatePremiumKeys.all, 'list'] as const,
  list: (input: CandidatePremiumJobsInput) =>
    [
      ...candidatePremiumKeys.lists(),
      input.jobType ?? null,
      input.status ?? null,
      input.product ?? null,
      input.paymentOrderId ?? null,
      input.limit ?? null,
      input.enabled ?? true,
    ] as const,
  detail: (id: string) => [...candidatePremiumKeys.all, 'detail', id] as const,
} as const;

function makeRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function resolveClient(supabase: CandidatePremiumSupabaseClient): SupabaseClientLike {
  if (supabase) return supabase;
  return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseClientLike;
}

async function getAuthHeaders(
  supabase: CandidatePremiumSupabaseClient,
): Promise<Record<string, string>> {
  const { data } = await resolveClient(supabase).auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: CandidatePremiumSupabaseClient,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const authHeaders = await getAuthHeaders(supabase);
  const headers = new Headers(init?.headers);
  headers.set('content-type', headers.get('content-type') ?? 'application/json');
  headers.set('x-request-id', headers.get('x-request-id') ?? makeRequestId());
  for (const [key, value] of Object.entries(authHeaders)) {
    headers.set(key, value);
  }
  const res = await fetch(buildFunctionUrl(path), { ...init, headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { code?: string; message?: string; request_id?: string };
    };
    throw new CandidatePremiumApiError(
      body.error?.message ?? res.statusText,
      res.status,
      body.error?.code,
      res.headers.get('x-request-id') ?? body.error?.request_id ?? undefined,
    );
  }
  return res.json() as Promise<T>;
}

function buildListPath(input: CandidatePremiumJobsInput): string {
  const params = new URLSearchParams();
  if (input.jobType) params.set('job_type', input.jobType);
  if (input.status) params.set('status', input.status);
  if (input.product) params.set('product', input.product);
  if (input.paymentOrderId) params.set('payment_order_id', input.paymentOrderId);
  if (input.limit) params.set('limit', String(input.limit));
  const query = params.toString();
  return query ? `candidate-premium?${query}` : 'candidate-premium';
}

export function useCandidatePremiumJobs(
  supabase: CandidatePremiumSupabaseClient,
  input: CandidatePremiumJobsInput = {},
) {
  return useQuery({
    enabled: input.enabled ?? true,
    queryKey: candidatePremiumKeys.list(input),
    queryFn: async () => {
      const body = await apiFetch<{ jobs: CandidatePremiumJob[]; ok: boolean }>(
        supabase,
        buildListPath(input),
      );
      return body.jobs;
    },
    staleTime: 15_000,
  });
}

export function useCandidatePremiumJob(
  supabase: CandidatePremiumSupabaseClient,
  id: string | null | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<CandidatePremiumJob, Error> & {
  job: CandidatePremiumJob | null;
  loading: boolean;
} {
  const query = useQuery({
    enabled: Boolean(id) && (options.enabled ?? true),
    queryKey: candidatePremiumKeys.detail(id ?? ''),
    queryFn: async () => {
      const body = await apiFetch<{ job: CandidatePremiumJob; ok: boolean }>(
        supabase,
        `candidate-premium/${encodeURIComponent(id ?? '')}`,
      );
      return body.job;
    },
    staleTime: 15_000,
  });

  return Object.assign(query, {
    job: query.data ?? null,
    loading: query.isLoading,
  });
}

export function useStartCandidatePremiumPrecheckSlot(supabase: CandidatePremiumSupabaseClient) {
  return useMutation({
    mutationFn: async (input: StartCandidatePremiumPrecheckSlotInput) => {
      const body = await apiFetch<{ job: CandidatePremiumJob; ok: boolean }>(
        supabase,
        'candidate-premium/precheck-slots',
        {
          body: JSON.stringify({
            parentJobId: input.parentJobId,
            sourceJobSlug: input.sourceJobSlug ?? null,
          }),
          method: 'POST',
        },
      );
      return body.job;
    },
  });
}
